import { Grid } from '@mui/material'
import { useFormikContext } from 'formik'
import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ISkillProject } from '../../../../interfaces/skills/ISkills'
import { useStore } from '../../../../Provider'
import { OPCheckBox } from '../../../OPcomponents/OPCheckBox'
import { OPDateField } from '../../../OPcomponents/OPDateField'
import { OPMultiline } from '../../../OPcomponents/OPMultiline'
import { OPNumberField } from '../../../OPcomponents/OPNumberField'
import { OPRadioButton } from '../../../OPcomponents/OPRadioButton'
import { OPSkillsCombobox } from '../../../OPcomponents/OPSkillsCombobox'
import ProjectSkillLink from '../newpubproject/ProjectSkillLink'
import { createMandaysOnChangeHandler } from '../workHistoryHelpers'
import { initValues } from './AddNewPrivateProject'

interface NewPersonalPrivateProjectProps {
  setProjectSkills: (skills: ISkillProject[]) => void
  projectSkills: ISkillProject[]
  deleteProjectConfirmation?: boolean
  isNewProject?: boolean
}

const NewPersonalPrivateProject = (props: NewPersonalPrivateProjectProps): JSX.Element => {
  const { setProjectSkills, projectSkills, deleteProjectConfirmation, isNewProject } = props

  const { t } = useTranslation()
  const rootStore = useStore()
  const { dropdownRoles, getAllRoles } = rootStore.projectStore

  useEffect(() => {
    getAllRoles()
  }, [getAllRoles])

  const radioOptions = [
    {
      value: '1',
      label: t('Projects.ManDays'),
    },
    {
      value: '0',
      label: t('Projects.ManMonths'),
    },
  ]

  const { values, handleChange, setFieldValue, setValues, errors, touched, setTouched } = useFormikContext<initValues>()

  return (
    <Grid container gap={4}>
      <Grid xs={12} item>
        <span className='myskill--font__titlefont sini'>{t('Projects.ProjectForMe')}</span>
      </Grid>
      <Grid container spacing={4}>
        <Grid item md={6} sm={12}>
          <OPSkillsCombobox
            name='userProject.roleIds'
            label={t('Projects.ProjectRoles')}
            placeholder={t('Placeholders.Choose')}
            multiple
            disabled={deleteProjectConfirmation}
            onDelete={(v) =>
              setFieldValue(
                'userProject.roleIds',
                values.userProject.roleIds.filter((role) => role.key !== v.option.key),
              )
            }
            groupBy={(opt: { categoryName?: string }) => opt.categoryName || t('Skills.Category')}
            optionList={dropdownRoles}
            onChange={(e, v) => setFieldValue('userProject.roleIds', v)}
            curState={values.userProject.roleIds}
          />
        </Grid>
        <Grid item md={3} sm={12}>
          <OPDateField
            name='userProject.startDate'
            label={`${t('Projects.EmployerStartDate')}*`}
            value={values.userProject.startDate}
            error={touched.userProject?.startDate && Boolean(errors.userProject?.startDate)}
            errMessage={t(errors.userProject?.startDate || '')}
            disabled={deleteProjectConfirmation}
            onChange={(newValue: Date | null) => {
              setTouched({ ...touched, userProject: { ...touched.userProject, startDate: true } })
              setFieldValue('userProject.startDate', newValue)
            }}
          />
        </Grid>
        <Grid item md={3} sm={12}>
          <OPDateField
            name='userProject.endDate'
            label={t('Projects.EmployerEndDate')}
            minDate={values.userProject.startDate}
            value={values.userProject.endDate}
            error={touched.userProject?.endDate && Boolean(errors.userProject?.endDate)}
            errMessage={t(errors.userProject?.endDate || '')}
            disabled={deleteProjectConfirmation}
            onChange={(newValue: Date | null) => {
              setTouched({ ...touched, userProject: { ...touched.userProject, endDate: true } })
              setFieldValue('userProject.endDate', newValue)
            }}
          />
        </Grid>
        <Grid container item xs={12} spacing={2} alignItems='center'>
          <Grid item md={3} sm={6}>
            {values.userProject.usingManDays ? (
              <OPNumberField
                label={`${t('Projects.ProjectWorkAmount')}${values.userProject.roleIds.length > 0 || projectSkills.length > 0 ? '*' : ''}`}
                name='userProject.manDays'
                value={values.userProject.manDays}
                error={Boolean(errors.userProject?.manDays)}
                errorText={t(errors.userProject?.manDays || '')}
                disabled={deleteProjectConfirmation}
                onChange={(e) => setFieldValue('userProject.manDays', e ?? 0)}
              />
            ) : (
              <OPNumberField
                label={`${t('Projects.ProjectWorkAmount')}${values.userProject.roleIds.length > 0 || projectSkills.length > 0 ? '*' : ''}`}
                name='userProject.manMonths'
                value={values.userProject.manMonths}
                error={Boolean(errors.userProject?.manMonths)}
                errorText={t(errors.userProject?.manMonths || '')}
                disabled={deleteProjectConfirmation}
                onChange={(e) => setFieldValue('userProject.manMonths', e ?? 0)}
              />
            )}
          </Grid>
          <Grid item className='griditem--formitem__radiomargin'>
            <OPRadioButton
              direction='row'
              name='userProject.usingManDays'
              radioOptions={radioOptions}
              label={''}
              disabled={deleteProjectConfirmation}
              onChange={createMandaysOnChangeHandler(values.userProject, (updated) => setValues({ ...values, userProject: updated }))}
              defaultVal={values.userProject.usingManDays ? '1' : '0'}
            />
          </Grid>
          <Grid item>
            <OPCheckBox
              name='userProject.isKeyProject'
              label={t('Projects.KeyProject')}
              value={values.userProject.isKeyProject ?? false}
              disabled={deleteProjectConfirmation}
              onChangeState={(_, checked) => setFieldValue('userProject.isKeyProject', checked)}
            />
          </Grid>
        </Grid>
        <Grid xs={12} item>
          <OPMultiline
            name='userProject.descriptionFi'
            label={t('Projects.WorkDescription')}
            onChange={handleChange}
            maxChars={1000}
            placeholder={t('Projects.WorkDescription')}
            minRows={6}
            value={values.userProject.descriptionFi}
            disabled={deleteProjectConfirmation}
            hasCounter
          />
        </Grid>
        <Grid xs={12} item>
          <OPMultiline
            name='userProject.descriptionEng'
            label={t('Projects.WorkDescriptionEng')}
            onChange={handleChange}
            maxChars={1000}
            placeholder={t('Projects.WorkDescriptionEng')}
            minRows={6}
            value={values.userProject.descriptionEng}
            disabled={deleteProjectConfirmation}
            hasCounter
          />
        </Grid>
        <Grid xs={12} item>
          <ProjectSkillLink
            isNewProject={isNewProject}
            projectId={values.userProject.projectId}
            deleteProjectConfirmation={deleteProjectConfirmation}
            setProjectSkills={setProjectSkills}
            curProjectSkills={projectSkills}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

export default observer(NewPersonalPrivateProject)
